import { useMemo } from "react";
import { ShipmentStatus, ShipmentIncompleteStatus } from "@/typedefs/shipment";
import { BillStatus, ShipmentStatusMap, IncompleteStatusMap, InvoiceStatus, VehicleType, VehicleTypeMap, SecurePassAutoSend, SecurePassAutoSendMap, SECURE_PASS_TAG_STATUS, SECURE_PASS_TAG_STATUS_MAP, SecurePassLogTypeMap, SecurePassLogType, ShipmentLocked, ShipmentLockedMap, NissanVehicleStatus, NissanVehicleStatusMap, } from "@/utils/enum";
import { filterName, filtersByTable, statusChildren } from "../constants";
const defaultLocation = {
    value: "location",
    label: "Location",
    children: [],
};
const defaultStatus = {
    value: "status",
    label: "Status",
    children: [],
};
const defaultFlagStatus = {
    value: "flagStatus",
    label: "Flag Status",
    children: [],
};
const defaultDropOffLocation = {
    value: "dropOffLocation",
    label: "DropOff Location",
    children: [],
};
const defaultPickupLocation = {
    value: "pickupLocation",
    label: "Pickup Location",
    children: [],
};
const defaultSpecialConditions = {
    value: "specialConditions",
    label: "Special Conditions",
    children: [],
};
const defaultVehicleType = {
    value: "vehicleType",
    label: "Vehicle Type",
    children: [],
};
const defaultSecurePass = {
    value: "securePass",
    label: "SecurePass™",
    children: [],
};
function cloneFilterOption(filterOption) {
    return JSON.parse(JSON.stringify(filterOption));
}
const useFilterOptions = ({ type, singleFilters, states, cities, dealers, managers, entitiesGroup, transporters, includedShipmentStatuses, includedIncompleteStatuses, loading, securePassEnabled, }) => useMemo(() => {
    const filterOptionsIds = filtersByTable[type];
    if (filterOptionsIds === undefined) {
        return [];
    }
    let location = null;
    let status = null;
    let flagStatus = null;
    let dealerIds = null;
    let managerIds = null;
    let isPublic = null;
    let rating = null;
    let entityGroups = null;
    let shipmentStatus = null;
    let dropOffLocation = null;
    let pickupLocation = null;
    const billStatus = null;
    const invoiceStatus = null;
    let transporterList = null;
    let specialConditions = null;
    let vehicleType = null;
    let securePass = null;
    let securePassLogType = null;
    const shipmentLocked = null;
    let nissanVehicleStatus = null;
    let nissanDropShipStatus = null;
    filterOptionsIds.forEach(filterId => {
        switch (filterId) {
            case "stateId":
            case "stateIds": {
                if (loading?.states) {
                    break;
                }
                location = location || cloneFilterOption(defaultLocation);
                location.children.push({
                    value: filterId,
                    label: filterName[filterId],
                    children: states.map(item => ({ label: item?.name, value: item?.id, children: [] })),
                });
                break;
            }
            case "city":
            case "cities": {
                if (loading?.cities) {
                    break;
                }
                location = location || cloneFilterOption(defaultLocation);
                location.children.push({
                    value: filterId,
                    label: filterName[filterId],
                    children: cities.map(item => ({ label: item, value: item, children: [] })),
                });
                break;
            }
            case "isActive":
            case "verified":
            case "hasTWIC":
            case "hasEModal":
            case "deactivated": {
                status = status || cloneFilterOption(defaultStatus);
                status.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: statusChildren.map(item => ({ label: item.label, value: item.value, children: [] })),
                });
                break;
            }
            case "isHardRoute":
            case "isOnHold":
            case "isProblemRide":
            case "isAutosledExpedited":
            case "requiresTWIC":
            case "requiresEModal":
            case "requiresLotExit":
            case "isEnclosed":
            case "isInoperational":
            case "isDirectBrokerage":
            case "isVisible": {
                specialConditions = specialConditions || cloneFilterOption(defaultSpecialConditions);
                specialConditions.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: statusChildren.map(item => ({ label: item.label, value: item.value, children: [] })),
                });
                break;
            }
            case "active":
            case "duplicate":
            case "block":
            case "missingInfo": {
                flagStatus = flagStatus || cloneFilterOption(defaultFlagStatus);
                flagStatus.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: [],
                });
                break;
            }
            case "dealerId": {
                if (loading?.dealers || dealers.length === 0) {
                    break;
                }
                dealerIds = {
                    label: filterName[filterId],
                    value: filterId,
                    children: dealers.map(dealer => ({ label: dealer.name, value: dealer.id, children: [] })),
                };
                break;
            }
            case "managerId": {
                if (loading?.managers || managers.length === 0) {
                    break;
                }
                managerIds = {
                    label: filterName[filterId],
                    value: filterId,
                    children: managers.map(manager => ({ label: manager.fullName, value: manager.id, children: [] })),
                };
                break;
            }
            case "isPublic": {
                isPublic = {
                    label: filterName[filterId],
                    value: filterId,
                    children: statusChildren.map(item => ({ label: item.label, value: item.value, children: [] })),
                };
                break;
            }
            case "rating": {
                rating = {
                    value: "rating",
                    label: "Rating",
                    children: [
                        {
                            value: "norated",
                            label: "Not Rated",
                            children: [],
                        },
                    ].concat(new Array(6).fill(0).map((value, index) => ({
                        value: `rate-${index}`,
                        label: `${index} Stars`,
                        children: [],
                    }))),
                };
                break;
            }
            case "companyGroupId": {
                if (loading?.entitiesGroup || entitiesGroup.length === 0) {
                    break;
                }
                entityGroups = {
                    label: filterName[filterId],
                    value: filterId,
                    children: entitiesGroup.map(group => ({
                        label: group.name,
                        value: group.id,
                    })) ?? [],
                };
                break;
            }
            case "shipmentStatus": {
                shipmentStatus = shipmentStatus || cloneFilterOption(defaultStatus);
                const safeIncludedShipmentStatuses = includedShipmentStatuses ?? [
                    ShipmentStatus.Pending,
                    ShipmentStatus.Completed,
                    ShipmentStatus.Posted,
                    ShipmentStatus.Assigned,
                    ShipmentStatus.Delivered,
                    ShipmentStatus.Incomplete,
                ];
                shipmentStatus.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: safeIncludedShipmentStatuses.map(shipmentStatusIterator => ({
                        label: ShipmentStatusMap[shipmentStatusIterator],
                        value: shipmentStatusIterator,
                        children: [],
                    })),
                });
                break;
            }
            case "incompleteStatus": {
                shipmentStatus = shipmentStatus || cloneFilterOption(defaultStatus);
                const safeIncludedShipmentStatuses = includedIncompleteStatuses ?? [
                    ShipmentIncompleteStatus.INCIDENT,
                    ShipmentIncompleteStatus.MISINFORMATION,
                    ShipmentIncompleteStatus.NOT_AVAILABLE,
                    ShipmentIncompleteStatus.CANCELLED,
                    ShipmentIncompleteStatus.DRY_RUN,
                ];
                shipmentStatus.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: safeIncludedShipmentStatuses.map(incompleteStatusIterator => ({
                        label: IncompleteStatusMap[incompleteStatusIterator],
                        value: incompleteStatusIterator,
                        children: [],
                    })),
                });
                break;
            }
            case "pickupStateId": {
                if (loading?.states) {
                    break;
                }
                pickupLocation = pickupLocation || cloneFilterOption(defaultPickupLocation);
                pickupLocation.children.push({
                    value: "pickupStateId",
                    label: "State",
                    children: states.map(item => ({
                        label: item?.name,
                        value: item?.id,
                        children: [],
                    })) ?? [],
                });
                break;
            }
            case "pickupCity": {
                if (loading?.cities) {
                    break;
                }
                pickupLocation = pickupLocation || cloneFilterOption(defaultPickupLocation);
                pickupLocation.children.push({
                    value: "pickupCity",
                    label: "City",
                    children: cities.map(item => ({
                        label: item,
                        value: item,
                        children: [],
                    })) ?? [],
                });
                break;
            }
            case "dropOffStateId": {
                if (loading?.states) {
                    break;
                }
                dropOffLocation = dropOffLocation || cloneFilterOption(defaultDropOffLocation);
                dropOffLocation.children.push({
                    value: "dropOffStateId",
                    label: "State",
                    children: states.map(item => ({
                        label: item?.name,
                        value: item?.id,
                        children: [],
                    })) ?? [],
                });
                break;
            }
            case "dropOffCity": {
                if (loading?.cities) {
                    break;
                }
                dropOffLocation = dropOffLocation || cloneFilterOption(defaultDropOffLocation);
                dropOffLocation.children.push({
                    value: "dropOffCity",
                    label: "City",
                    children: cities.map(item => ({
                        label: item,
                        value: item,
                        children: [],
                    })) ?? [],
                });
                break;
            }
            case "billStatus": {
                status = status || cloneFilterOption(defaultStatus);
                status.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: [
                        {
                            label: "Unpaid",
                            value: BillStatus.Unpaid,
                            children: [],
                        },
                        {
                            label: "Paid",
                            value: BillStatus.Paid,
                            children: [],
                        },
                        {
                            label: "Pending",
                            value: BillStatus.Pending,
                            children: [],
                        },
                        {
                            label: "Load Issues",
                            value: BillStatus.LoadIssues,
                            children: [],
                        },
                        {
                            label: "ACH Issues",
                            value: BillStatus.ACHIssues,
                            children: [],
                        },
                    ],
                });
                break;
            }
            case "invoiceStatus": {
                status = status || cloneFilterOption(defaultStatus);
                status.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: [
                        {
                            label: "Not Received",
                            value: InvoiceStatus.NotReceived,
                            children: [],
                        },
                        {
                            label: "Billed",
                            value: InvoiceStatus.Billed,
                            children: [],
                        },
                        {
                            label: "Received",
                            value: InvoiceStatus.Received,
                            children: [],
                        },
                        {
                            label: "Pending",
                            value: InvoiceStatus.Pending,
                            children: [],
                        },
                    ],
                });
                break;
            }
            case "transporterId": {
                if (loading?.transporters || (transporters?.length ?? 0) === 0) {
                    break;
                }
                transporterList = {
                    label: filterName[filterId],
                    value: filterId,
                    children: transporters.map(transporter => ({
                        label: transporter.name,
                        value: transporter.id,
                    })) ?? [],
                };
                break;
            }
            case "vehicleType": {
                vehicleType = cloneFilterOption(defaultVehicleType);
                vehicleType.children = [
                    VehicleType.Car,
                    VehicleType.Truck,
                    VehicleType.SUV,
                    VehicleType.Van,
                    VehicleType.ATV_UTV,
                    VehicleType.Motorcycle,
                    VehicleType.Other,
                ].map(vehicleTypeIterator => ({
                    label: VehicleTypeMap[vehicleTypeIterator],
                    value: vehicleTypeIterator,
                    children: [],
                }));
                break;
            }
            case "securePassMissingContact": {
                if (!securePassEnabled)
                    break;
                securePass = securePass || cloneFilterOption(defaultSecurePass);
                securePass.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: [...statusChildren],
                });
                break;
            }
            case "securePassAutoSendType": {
                if (!securePassEnabled)
                    break;
                securePass = securePass || cloneFilterOption(defaultSecurePass);
                securePass.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: [SecurePassAutoSend.NOT_SEND, SecurePassAutoSend.DEFAULT, SecurePassAutoSend.SEND].map(autosendType => ({
                        label: SecurePassAutoSendMap[autosendType],
                        value: autosendType,
                        children: [],
                    })),
                });
                break;
            }
            case "securePassValue": {
                if (!securePassEnabled)
                    break;
                securePass = {
                    label: filterName[filterId],
                    value: filterId,
                    children: [
                        SECURE_PASS_TAG_STATUS.Enabled,
                        SECURE_PASS_TAG_STATUS.Disabled,
                        SECURE_PASS_TAG_STATUS.MissingContact,
                    ].map(securePassStatus => ({
                        label: SECURE_PASS_TAG_STATUS_MAP[securePassStatus],
                        value: securePassStatus,
                        children: [],
                    })),
                };
                break;
            }
            case "securePassLogType": {
                if (!securePassEnabled)
                    break;
                securePassLogType = {
                    label: filterName[filterId],
                    value: filterId,
                    children: Object.values(SecurePassLogType).map(logType => ({
                        label: SecurePassLogTypeMap[logType],
                        value: logType,
                        children: [],
                    })),
                };
                break;
            }
            case "shipmentLocked": {
                shipmentStatus = shipmentStatus || cloneFilterOption(defaultStatus);
                shipmentStatus.children.push({
                    label: filterName[filterId],
                    value: filterId,
                    children: Object.values(ShipmentLocked).map(lockedOption => ({
                        label: ShipmentLockedMap[lockedOption],
                        value: lockedOption,
                        children: [],
                    })),
                });
                break;
            }
            case "nissanVehicleStatus": {
                nissanVehicleStatus = {
                    label: filterName[filterId],
                    value: filterId,
                    children: Object.values(NissanVehicleStatus).map(vehicleStatus => ({
                        label: NissanVehicleStatusMap[vehicleStatus],
                        value: vehicleStatus,
                        children: [],
                    })),
                };
                break;
            }
            case "nissanDropShipStatus": {
                nissanDropShipStatus = {
                    label: filterName[filterId],
                    value: filterId,
                    children: statusChildren.map(item => ({ label: item.label, value: item.value, children: [] })),
                };
                break;
            }
            default:
                break;
        }
    });
    if (singleFilters) {
        const categories = [location, status, flagStatus].filter(category => category !== null);
        const optionsSingle = categories.reduce((acc, category) => [...acc, ...category.children], []);
        return [...optionsSingle, dealerIds, isPublic, rating, managerIds, nissanDropShipStatus].filter(category => category !== null);
    }
    const options = [
        location,
        status,
        specialConditions,
        flagStatus,
        dealerIds,
        managerIds,
        isPublic,
        rating,
        entityGroups,
        shipmentStatus,
        dropOffLocation,
        pickupLocation,
        billStatus,
        invoiceStatus,
        transporterList,
        vehicleType,
        securePass,
        securePassLogType,
        shipmentLocked,
        nissanVehicleStatus,
        nissanDropShipStatus,
    ].filter(category => category !== null);
    return options;
}, [
    type,
    singleFilters,
    loading?.states,
    loading?.cities,
    loading?.dealers,
    loading?.managers,
    loading?.entitiesGroup,
    loading?.transporters,
    states,
    cities,
    dealers,
    managers,
    entitiesGroup,
    includedShipmentStatuses,
    includedIncompleteStatuses,
    transporters,
    securePassEnabled,
]);
export default useFilterOptions;
